import { useCallback } from 'react';
import { graphql, useMutation } from 'react-relay';
import { useShowErrorFlag } from '@atlassian/jira-personal-settings-error-flag/src/ui/index.tsx';
import type {
	globalNotificationsConfigRow_updateGlobalPreferencesMutation,
	globalNotificationsConfigRow_updateGlobalPreferencesMutation$data,
} from '@atlassian/jira-relay/src/__generated__/globalNotificationsConfigRow_updateGlobalPreferencesMutation.graphql';
import type { JiraNotificationType } from '@atlassian/jira-relay/src/__generated__/notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences.graphql';
import type { JiraNotificationChannelType } from '@atlassian/jira-relay/src/__generated__/notificationsConfigRow_personalSettingsNotificationsConfigTable_preference.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { fireOnCompleteErrorAnalytics, fireOnErrorAnalytics } from '../../../common/utils.tsx';

export const useGlobalRowMutation = () => {
	const cloudId = useCloudId();
	const { showErrorFlag } = useShowErrorFlag();
	const [commit] = useMutation<globalNotificationsConfigRow_updateGlobalPreferencesMutation>(
		graphql`
			mutation globalNotificationsConfigRow_updateGlobalPreferencesMutation(
				$cloudId: ID!
				$input: JiraUpdateGlobalNotificationPreferencesInput!
			) {
				jira {
					updateGlobalNotificationPreferences(cloudId: $cloudId, input: $input)
						@optIn(to: "JiraNotificationPreference") {
						success
						errors {
							message
						}
						preferences {
							...notificationsConfigRow_personalSettingsNotificationsConfigTable_allPreferences
							issueAssigned {
								...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
							}
							issueUpdated {
								...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
							}
							issueDeleted {
								...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
							}
							commentCreated {
								...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
							}
							commentEdited {
								...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
							}
							commentDeleted {
								...notificationsConfigRow_personalSettingsNotificationsConfigTable_preference
							}
						}
					}
				}
			}
		`,
	);

	return useCallback(
		(
			channel: JiraNotificationChannelType,
			isEnabled: boolean,
			newPreferences: {
				[key: string]: {
					id: string;
					type: JiraNotificationType | null | undefined;
					emailChannel: {
						channel: JiraNotificationChannelType | null | undefined;
						isEnabled: boolean | null | undefined;
						isEditable: boolean | null | undefined;
					};
				};
			},
			type: JiraNotificationType,
		) => {
			commit({
				variables: {
					cloudId,
					input: {
						preferences: [
							{
								channel,
								isEnabled,
								type,
							},
						],
					},
				},
				onError: (error) => {
					showErrorFlag();
					fireOnErrorAnalytics(error, 'Row', 'global', channel);
				},
				onCompleted: (
					response: globalNotificationsConfigRow_updateGlobalPreferencesMutation$data,
				) => {
					const errors = response?.jira?.updateGlobalNotificationPreferences?.errors;
					if (errors && errors?.length > 0) {
						showErrorFlag();
						fireOnCompleteErrorAnalytics(errors, 'Row', 'global', channel);
					}
				},
				optimisticResponse: {
					jira: {
						updateGlobalNotificationPreferences: {
							success: true,
							errors: [],
							preferences: {
								...newPreferences,
							},
						},
					},
				},
			});
		},
		[cloudId, commit, showErrorFlag],
	);
};
